import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Pencil, Eye, Mail, ImagePlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailSignatureEditor() {
  const [signature, setSignature] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    loadSignature();
  }, []);

  const loadSignature = async () => {
    try {
      const res = await axios.get(`${API}/user/preferences/email_signature`);
      setSignature(res.data.value || "");
    } catch (err) {
      // No signature saved yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const html = editorRef.current?.innerHTML || "";
      await axios.put(`${API}/user/preferences/email_signature`, {
        value: html
      });
      setSignature(html);
      setEditing(false);
      toast.success("Signature saved");
    } catch (err) {
      toast.error("Failed to save signature");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/preferences/email_signature`, {
        value: ""
      });
      setSignature("");
      setEditing(false);
      toast.success("Signature removed");
    } catch (err) {
      toast.error("Failed to remove signature");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = useCallback(() => {
    setEditing(true);
    // Set content after the div renders
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = signature;
        editorRef.current.focus();
      }
    }, 50);
  }, [signature]);

  const handlePaste = useCallback(async (e) => {
    const clipboardData = e.clipboardData;

    // Check for pasted image files (screenshots, drag-drop)
    const imageFile = Array.from(clipboardData.files || []).find(f => f.type.startsWith('image/'));
    if (imageFile) {
      e.preventDefault();
      const reader = new FileReader();
      reader.onload = () => {
        document.execCommand('insertHTML', false, `<img src="${reader.result}" style="max-width:200px;" />`);
      };
      reader.readAsDataURL(imageFile);
      return;
    }

    const html = clipboardData.getData('text/html');
    if (html) {
      e.preventDefault();
      // Clean up Outlook/Word HTML cruft
      let cleaned = html
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<o:p>[\s\S]*?<\/o:p>/g, '')
        .replace(/class="[^"]*"/g, '')
        .replace(/\s*mso-[^;"]*;?/g, '');

      // Convert cid: and blob: images to base64 from clipboard items
      const items = clipboardData.items;
      const imageItems = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          imageItems.push(items[i].getAsFile());
        }
      }

      if (imageItems.length > 0) {
        // Replace broken image srcs with base64 versions
        const parser = new DOMParser();
        const doc = parser.parseFromString(cleaned, 'text/html');
        const imgs = doc.querySelectorAll('img');
        let imgIdx = 0;
        for (const img of imgs) {
          const src = img.getAttribute('src') || '';
          if (src.startsWith('cid:') || src.startsWith('blob:') || !src.startsWith('http')) {
            if (imgIdx < imageItems.length && imageItems[imgIdx]) {
              const b64 = await new Promise(resolve => {
                const r = new FileReader();
                r.onload = () => resolve(r.result);
                r.readAsDataURL(imageItems[imgIdx]);
              });
              img.setAttribute('src', b64);
              img.style.maxWidth = '200px';
              imgIdx++;
            }
          }
        }
        cleaned = doc.body.innerHTML;
      }

      document.execCommand('insertHTML', false, cleaned);
    }
  }, []);

  const handleImageInsert = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (editorRef.current) {
        editorRef.current.focus();
        document.execCommand('insertHTML', false, `<img src="${reader.result}" style="max-width:200px;" />`);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <Pencil className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Email Signature</CardTitle>
              <CardDescription>Your signature will be appended to emails sent from the CRM</CardDescription>
            </div>
          </div>
          {!editing && (
            <Button variant="outline" size="sm" onClick={startEditing}>
              <Pencil className="h-4 w-4 mr-1" />
              {signature ? "Edit" : "Add Signature"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : editing ? (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start justify-between gap-3">
              <p className="text-xs text-blue-700">
                <strong>Tip:</strong> For best results, copy your signature from Outlook Web (outlook.office.com) or Gmail in your browser. Images and formatting will be preserved. Use the image button to add logos manually.
              </p>
              <Button variant="outline" size="sm" className="shrink-0 text-xs h-7" onClick={() => imageInputRef.current?.click()}>
                <ImagePlus className="h-3.5 w-3.5 mr-1" /> Image
              </Button>
              <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageInsert} className="hidden" />
            </div>
            <div
              ref={editorRef}
              contentEditable
              onPaste={handlePaste}
              className="min-h-[120px] max-h-[300px] overflow-y-auto border rounded-lg p-4 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              style={{ lineHeight: '1.5' }}
            />
            <div className="flex justify-between">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                {signature && (
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleDelete} disabled={saving}>
                    <Trash2 className="h-4 w-4 mr-1" /> Remove
                  </Button>
                )}
              </div>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save Signature
              </Button>
            </div>
          </div>
        ) : signature ? (
          <div className="border rounded-lg p-4 bg-slate-50">
            <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
              <Eye className="h-3 w-3" /> Preview
            </div>
            <div dangerouslySetInnerHTML={{ __html: signature }} className="prose prose-sm max-w-none" />
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <Mail className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No signature set</p>
            <p className="text-xs mt-1">Add your email signature to include it when sending emails to leads</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
